import { FlyoutContext } from "@app/shared/Flyouts";
import { JSX, useContext, useState, FC, useEffect } from "react";
import { processKeyDownTextValidation, SpecialtyGroupModel } from "@app/admin/manage-specialties/specialty";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ModalLink } from "@app/shared/Modals";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { InputValidationRegExps } from "@app/projects/enumerators";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

interface CreatePanelProps {
  currentUserid: number;
  selectedRow: SpecialtyGroupModel;
  setIsEdited: React.Dispatch<React.SetStateAction<boolean>>;
  setRollUpFlyoutData: React.Dispatch<React.SetStateAction<SpecialtyGroupModel>>;
}

interface SpecialtyRollUpItem {
  code: string;
  name: string;
  displayOrder: number;
  weight: string;
  viewOnly: boolean;
}

interface SpecialtyModel {
  code: string;
  name: string;
  displayOrder: number;
  weight: string;
}

interface SpecialtyGroupTableData {
  specialtyCode: JSX.Element;
  specialtyName: JSX.Element;
  edit: JSX.Element;
  remove: JSX.Element;
  [wafflesKeyColumnName]: string;
  reorder: JSX.Element;
}

const specialtyRollupDropdownOptions: { key: string, name: string }[] =
  [
    {
      key: "moveUp",
      name: "Move Up",
    },
    {
      key: "moveDown",
      name: "Move Down",
    },
    {
      key: "moveTop",
      name: "Move To Top",
    },
    {
      key: "moveBottom",
      name: "Move To Bottom",
    },
  ];

const SpecialtyRollupFlyout: FC<CreatePanelProps> = (props: CreatePanelProps) => {
  const getInitialSpecialtyRollUpProperties = (): SpecialtyRollUpItem[] => {
    const newSpecialtyRollUpLineItems = props.selectedRow.specialties.map((item) => ({ ...item, viewOnly: true }))
    if (props.selectedRow.specialties.every((x) => (x.displayOrder === 1 || x.displayOrder === 0))) {
      newSpecialtyRollUpLineItems.forEach(element => {
        element.displayOrder = newSpecialtyRollUpLineItems.indexOf(element) + 1;
      });
    } else {
      newSpecialtyRollUpLineItems.map((x) => (
        {
          code: x.code,
          displayOrder: x.displayOrder,
          name: x.name,
          weight: x.weight
        }
      ))
    }
    return newSpecialtyRollUpLineItems;
  };

  const flyoutContext = useContext(FlyoutContext);
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [isEdited, setIsEdited] = useState(false);
  const [isNameValid, setIsNameValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [specialtyGroupTableData, setSpecialtyGroupTableData] = useState<SpecialtyGroupTableData[]>([]);
  const [specialtyRollUpItemCode, setSpecialtyRollUpItemCode] = useState("");
  const [specialtyRollUpItemName, setSpecialtyRollUpItemName] = useState("");
  const [specialtyRollUpProperties, setSpecialtyRollUpProperties] = useState<SpecialtyRollUpItem[]>(getInitialSpecialtyRollUpProperties());

  useEffect(() => {
    setLoading(true);
    createSpecialityRollUpWafflesTable(specialtyRollUpProperties).then(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    createSpecialityRollUpWafflesTable(specialtyRollUpProperties);
  }, [isCodeValid, isNameValid, specialtyRollUpItemCode, specialtyRollUpItemName, specialtyRollUpProperties]);

  const DisplayReorder = (row: SpecialtyModel) => {
    return specialtyRollupDropdownOptions.map((x) =>
      <NewActionMenuItem
        display={x.name}
        key={x.key}
        isSelected={false}
        onClick={() => {
          handleDisplayReorder(row, x.name);
          // this is done to close the flyout dropdown since by default after selecting an option it remained open
          $('[data-testId="new-action-menu__hideme-button"]').parent().trigger("click");
        }}
        testId="manage-specialty__download_template-option--json"
      />
    );
  }

  const handleDisplayReorder = (row: SpecialtyModel, operation: string) => {
    const specialtyRow = specialtyRollUpProperties;
    if (operation === 'Move Up' && (row.displayOrder !== 1)) {
      const rowToChange = specialtyRow.find((e) => e.displayOrder === (row.displayOrder - 1));
      rowToChange.displayOrder = row.displayOrder;
      row.displayOrder = row.displayOrder - 1;
    }
    if (operation === 'Move Down' && (row.displayOrder !== specialtyRow.length)) {
      const rowToChange = specialtyRow.find((e) => e.displayOrder === (row.displayOrder + 1));
      rowToChange.displayOrder = row.displayOrder;
      row.displayOrder = row.displayOrder + 1;
    }
    if (operation === 'Move To Top' && (row.displayOrder !== 1)) {
      specialtyRow.forEach(element => {
        if (element.displayOrder < row.displayOrder) {
          element.displayOrder = element.displayOrder + 1;
        }
      });
      row.displayOrder = 1;
    }
    if (operation === 'Move To Bottom' && (row.displayOrder !== specialtyRow.length)) {
      specialtyRow.forEach(element => {
        if (element.displayOrder > row.displayOrder) {
          element.displayOrder = element.displayOrder - 1;
        }
      });
      row.displayOrder = specialtyRow.length;
    }
    createSpecialityRollUpWafflesTable(specialtyRow);
    const specialtyModel = props.selectedRow;
    specialtyModel.specialties = specialtyRow;
    setIsEdited(true);
    props.setIsEdited(true);
  };

  const saveRollUpItems = () => {
    const specialties = specialtyRollUpProperties.map(property => ({ code: property.code, displayOrder: property.displayOrder, name: property.name, weight: property.weight }));

    props.setRollUpFlyoutData({ ...props.selectedRow, specialties });
    flyoutContext.handleCloseFlyout();
  }

  const handleSpecialtyRollUpItemSave = (specialty: SpecialtyRollUpItem[], rec: SpecialtyRollUpItem) => {
    const modifiedSpecialty = specialty.map(item => {
      if (!item.viewOnly) {
        const code = specialtyRollUpItemCode;
        const name = specialtyRollUpItemName;
        return { ...rec, code: (code === "") ? rec.code : code, name: (name === "") ? rec.name : name, viewOnly: true };
      } else {
        return item;
      }
    });

    createSpecialityRollUpWafflesTable(modifiedSpecialty);
    setIsEdited(true);
    setSpecialtyRollUpProperties(modifiedSpecialty);
    props.setIsEdited(true);
  }

  const checkIfSpecialtyRollUpItemNameIsValid = (specialty: SpecialtyRollUpItem[], name: string) => {
    for (const item of specialty) {
      if (item.name === name.trim() && item.viewOnly) {
        setIsNameValid(false);
        setSpecialtyRollUpItemName(name.trim());
        return;
      }
    }
    setIsNameValid(true);
    setSpecialtyRollUpItemName(name.trim());
  }

  const checkIfSpecialtyRollUpItemCodeIsValid = (specialty: SpecialtyRollUpItem[], code: string) => {
    for (const item of specialty) {
      if (item.code === code.trim() && item.viewOnly) {
        setIsCodeValid(false);
        setSpecialtyRollUpItemCode(code.trim());
        return;
      }
    }
    setIsCodeValid(true);
    setSpecialtyRollUpItemCode(code.trim());
  }

  const removeSpecialtyRollUpItem = (rec: SpecialtyRollUpItem) => {
    const specialtyRow = specialtyRollUpProperties;
    specialtyRow.splice(specialtyRow.findIndex((x) => x.name === rec.name), 1);
    createSpecialityRollUpWafflesTable(specialtyRow);
    setIsEdited(true);
    setSpecialtyRollUpProperties(specialtyRow);
    props.setIsEdited(true);
  }

  const editRow = (specialty: SpecialtyRollUpItem[], rec: SpecialtyRollUpItem) => {
    const modifiedSpecialty = specialty.map(item => {
      if (item.name == rec.name) {
        return { ...item, viewOnly: false };
      } else {
        return item;
      }
    });
    setSpecialtyRollUpProperties(modifiedSpecialty);
    createSpecialityRollUpWafflesTable(modifiedSpecialty);
  }

  const createSpecialityRollUpWafflesTable = async (specialty: SpecialtyRollUpItem[]): Promise<SpecialtyGroupTableData[]> => {
    const anySelection = !specialty?.every(sg => sg.viewOnly);
    const rowData = specialty.sort((a, b) => a.displayOrder - b.displayOrder).map(rec => {
      /* oxlint-disable sort-keys */
      return ({
        key: rec.displayOrder.toString(),
        specialtyName: ((!rec.viewOnly &&
          <QESTextInput
            label="Specialty name"
            testId="edit-specialtyRollUp_specialtyName"
            defaultValue={rec.name}
            hideLabel={true}
            isValid={isNameValid}
            unrequiredValidation={true}
            errorText={"not a valid name"}
            onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
            onChange={(e) => { checkIfSpecialtyRollUpItemNameIsValid(specialty, e.target.value) }}
          />) || (<>{rec.name}</>)),
        specialtyCode: ((!rec.viewOnly &&
          <QESTextInput
            label="Specialty Code"
            testId="edit-specialtyRollUp_specialtyCode"
            defaultValue={rec.code}
            hideLabel={true}
            isValid={isCodeValid}
            unrequiredValidation={true}
            errorText={"not a valid code"}
            onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName) }}
            onChange={(e) => { checkIfSpecialtyRollUpItemCodeIsValid(specialty, e.target.value) }}
          />) || (<>{rec.code}</>)),
        edit: ((!rec.viewOnly &&
          <QESButton
            qesType="secondary"
            qesTestId="edit-specialtyRollUp__edit-button"
            onClick={() => handleSpecialtyRollUpItemSave(specialty, rec)}
            disabled={!isCodeValid || !isNameValid}
          >
            Save
          </QESButton>) || (rec.viewOnly && anySelection &&
            <QESButton
              qesType="secondary"
              qesTestId="edit-specialtyGroup__edit-button"
              disabled={true}
            >
              <QesIcon iconName={FontAwesome.Edit} description="Edit" />
            </QESButton>) || (
            <QESButton
              qesType="secondary"
              qesTestId="edit-specialtyGroup__edit-button"
              onClick={() => editRow(specialty, rec)}
            >
              <QesIcon iconName={FontAwesome.Edit} description="Edit" />
            </QESButton>)),
        remove: (
          <ModalLink
            classNames="qes-button-secondary danger"
            content={{
              body: "Are you sure you want to remove this specialty rollup item?",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => removeSpecialtyRollUpItem(rec),
              confirmButtonText: "Remove",
              confirmButtonType: "primary-danger",
              title: `Remove Specialty Roll Up Item`,
            }}
            testId="manage-specialtyGroup__remove-specialtyRollUpItem-modal"
            hideBodyScroll={true}
            disabled={false}
          >
            Remove
          </ModalLink>
        ),
        reorder: (
          <NewActionMenu
            testId="hideme"
            buttonText={'...'}
            containerClassnames="col-8"
            buttonClassNames="qes-button-secondary"
            fixMenu={true}
            closeOnClick={true}
            fixedMenuOffset={{
              x: -600,
              y: -15
            }}
            hideArrow={true}
          >
            {DisplayReorder(rec)}
          </NewActionMenu>
        )
      });
      /* oxlint-enable sort-keys */
    });
    setSpecialtyGroupTableData(rowData);
    return rowData;
  }

  /* oxlint-disable sort-keys */
  const columnConfig: ColumnConfig = {
    specialtyName: {
      displayText: "Specialty Name",
      sizeContentToFit: true,
      testId: "specialtyRollUp-specialtyName",
      noSort: true
    },
    specialtyCode: {
      displayText: "Specialty Code",
      sizeContentToFit: true,
      testId: "specialtyRollUp-specialtycode",
      noSort: true
    },
    edit: {
      displayText: "Edit",
      sizeContentToFit: true,
      testId: "specialtyRollUp-edit",
      noSort: true
    },
    remove: {
      displayText: "Remove",
      sizeContentToFit: true,
      testId: "specialtyRollup-remove",
      noSort: true
    },
    reorder: {
      displayText: "Reorder Specialty",
      sizeContentToFit: true,
      testId: "specialtyRollUp-reorder",
      noSort: true
    },
  };
  /* oxlint-enable sort-keys */

  const handleDeleteSpecialtyRollUp = () => {
    props.selectedRow.specialties.splice(0, props.selectedRow.specialties.length);
    props.setIsEdited(true);
    flyoutContext.handleCloseFlyout();
  };

  return (
    <>
      {loading ? <LoadingSpinner /> :
        <Page documentTitle="Specialties Roll-Ups">
          <PageHeader title={props.selectedRow.name} />
          <ModalLink
            classNames="qes-button-secondary danger"
            content={{
              body: "Are you sure you want to delete this specialty?",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => handleDeleteSpecialtyRollUp(),
              confirmButtonText: "Discard",
              confirmButtonType: "primary-danger",
              title: `Delete Specialty`,
            }}
            testId="manage-specialty__cancel-delete-modal"
            hideBodyScroll={true}
          >
            Delete
          </ModalLink>
          <QESButton
            qesType="secondary"
            qesTestId="save-specialtyGroup_calculation_type__save-button"
            style={{ display: "" }}
            onClick={() => { saveRollUpItems() }}
            disabled={!isEdited}
            className="ms-5"
          >
            Save
          </QESButton>
          <div className="row mt-3">
            <div className="col-12" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }} >
              <div style={{ minWidth: "100%", paddingLeft: "0%", paddingRight: "2%" }}>
                <Waffles<SpecialtyGroupTableData>
                  columns={columnConfig}
                  data={specialtyGroupTableData}
                  enablePagination={false}
                  fullWidth={true}
                  loading={loading}
                  passThroughSorting={true}
                  testId="client-manage-specialtyGroup-waffles-table" />
              </div>
            </div>
          </div>
        </Page>
      }
    </>
  )
}

export default SpecialtyRollupFlyout;
